import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useRouteUnmountEffect, withRouter} from '@computerrock/formation-router';
import {useTranslate} from '@computerrock/formation-i18n';
import {ContentBlock, ContentItem, useStyles} from '@ace-de/ui-components';
import {apmContractPartnerAssignmentChannelTypes, efServiceAssignmentTypes} from '@ace-de/eua-entity-types';
import CommissioningBasicDataPanel from '../service-assignments/ui-elements/CommissioningBasicDataPanel';
import * as serviceAssignmentSelectors from '../service-assignments/serviceAssignmentSelectors';
import * as userProfileSelectors from '../user-profiles/userProfileSelectors';
import config from '../config';
import CommissioningCommissioningPanel from '../service-assignments-medical-clearance/ui-elements/CommissioningCommissioningPanel';
import CommissioningContactPersonPanel from '../service-assignments/ui-elements/CommissioningContactPersonPanel';
import CommissioningDiagnoseAndInsurancePanel from '../service-assignments/ui-elements/CommissioningDiagnoseAndInsurancePanel';
import DocumentUploadPanel from '../service-cases/ui-elements/DocumentUploadPanel';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import CommissioningACEPartnerPanel from '../service-assignments/ui-elements/CommissioningACEPartnerPanel';
import * as serviceAssignmentActionTypes from '../service-assignments/serviceAssignmentActionTypes';
import * as samaActionTypes from './samaActionTypes';
import isACEPartnerFromDACH from '../service-assignments/isACEPartnerFromDACH';

const SAMACommissioningTab = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand, translate} = useTranslate();
    const translateTab = createTranslateShorthand('sama_commissioning_tab');
    const {serviceAssignment, users, serviceCase} = props;
    const {submitSAMACommissioningServiceProviderForm, submitSAMACommissioningForm, submitCreateSAMAPDF} = props;
    const {hasToResetAttachmentSelection, storeHasToResetAttachmentSelection} = props;
    const [callbackPhoneNo, setCallbackPhoneNo] = useState(serviceAssignment.callbackPhoneNo || '');
    const [fallbackSystemId, setFallbackSystemId] = useState(serviceAssignment?.fallbackSystemId || '');
    const [isFallbackSystemIdEnabled, setIsFallbackSystemIdEnabled] = useState(
        !!serviceAssignment?.isFallbackSystemIdEnabled,
    );
    const [selfPayer, setSelfPayer] = useState({
        hasSelfPayerOption: serviceAssignment.selfPayerOption,
        selfPayerNote: serviceAssignment.selfPayerNote,
    });
    const [assignmentText, setAssignmentText] = useState(serviceAssignment?.assignmentText || '');
    const [attachmentsToSendWithAssignment, setAttachmentsToSendWithAssignment] = useState([]);
    const [additionalInformationText, setAdditionalInformationText] = useState(
        serviceAssignment?.additionalInformationText || '',
    );

    useEffect(() => {
        if (!hasToResetAttachmentSelection) return;
        setAttachmentsToSendWithAssignment([]);
        storeHasToResetAttachmentSelection({hasToResetAttachmentSelection: false});
    }, [hasToResetAttachmentSelection, storeHasToResetAttachmentSelection, setAttachmentsToSendWithAssignment]);

    const onChangeHandler = (key, value) => {
        if (key === 'assignmentText') {
            setAssignmentText(value);
            return;
        }
        if (key === 'additionalInformationText') {
            setAdditionalInformationText(value);
            return;
        }
        if (key === 'fallbackSystemId') {
            setFallbackSystemId(value);
            return;
        }
        if (key === 'callbackPhoneNo') {
            setCallbackPhoneNo(value);
            return;
        }
        if (key === 'isFallbackSystemIdEnabled') {
            setIsFallbackSystemIdEnabled(value);
            setFallbackSystemId('');
            return;
        }
        if (key === 'hasSelfPayerOption') {
            setSelfPayer({
                ...selfPayer,
                [key]: value,
                selfPayerNote: '',
            });
            return;
        }
        if (key === 'selfPayerNote' && value.length <= config.MAXIMUM_SELF_PAYER_NOTE_LENGTH) {
            setSelfPayer({
                ...selfPayer,
                [key]: value,
            });
        }
    };

    const handleOnSend = serviceProviderData => {
        const serviceAssignmentData = {
            selfPayerOption: selfPayer.hasSelfPayerOption,
            selfPayerNote: selfPayer.selfPayerNote,
            assignmentText,
            additionalInformationText,
            callbackPhoneNo,
            fallbackSystemId,
            isFallbackSystemIdEnabled,
            ...serviceProviderData,
        };

        submitSAMACommissioningServiceProviderForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            assignmentType: efServiceAssignmentTypes.MEDICAL_ADVICE,
            serviceAssignmentData,
            attachmentsToSendWithAssignment,
        });

        // on send, reset attachments state: see https://computerrock.atlassian.net/browse/ACEECS-5792
        setAttachmentsToSendWithAssignment([]);
    };

    useRouteUnmountEffect(({completeRouteUnmountSideEffect}) => {
        if (!selfPayer) {
            completeRouteUnmountSideEffect({
                caller: samaActionTypes.SUBMIT_SAMA_COMMISSIONING_FORM,
            });
            return;
        }

        submitSAMACommissioningForm({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData: {
                selfPayerOption: selfPayer.hasSelfPayerOption,
                selfPayerNote: selfPayer.selfPayerNote,
                assignmentText,
                additionalInformationText,
                callbackPhoneNo,
                fallbackSystemId,
                isFallbackSystemIdEnabled,
            },
            attachmentsToSendWithAssignment,
        });
    }, [
        selfPayer,
        serviceAssignment.lineNo,
        serviceAssignment.serviceCaseId,
        assignmentText,
        additionalInformationText,
        callbackPhoneNo,
        fallbackSystemId,
        isFallbackSystemIdEnabled,
        submitSAMACommissioningForm,
        attachmentsToSendWithAssignment,
    ]);

    const generatePDF = serviceProviderData => {
        const serviceAssignmentData = {
            selfPayerOption: selfPayer.hasSelfPayerOption,
            selfPayerNote: selfPayer.selfPayerNote,
            assignmentText,
            additionalInformationText,
            callbackPhoneNo,
            fallbackSystemId,
            isFallbackSystemIdEnabled,
            ...serviceProviderData,
        };

        const isDACH = isACEPartnerFromDACH(serviceAssignment?.acePartner);

        submitCreateSAMAPDF({
            serviceAssignmentLineNo: serviceAssignment.lineNo,
            serviceCaseId: serviceAssignment.serviceCaseId,
            serviceAssignmentData,
            attachmentsToSendWithAssignment,
            prefix: serviceCase.prefix,
            serviceType: isDACH
                ? translate(`global.service_type.${serviceAssignment.assignmentType.toLowerCase()}`)
                : serviceAssignment.assignmentType.toLowerCase(),
            serviceAssignmentType: serviceAssignment.assignmentType,
        });
    };

    if (!serviceCase || !serviceAssignment) return null;

    return (
        <ContentBlock
            className={cx([
                'global!ace-u-height--full',
                'global!ace-u-max-height--full',
                'global!ace-u-flex--align-stretch',
                'global!ace-u-margin--top-0',
            ])}
        >
            <ContentItem className={cx('ace-c-content-item--span-9')}>
                <CommissioningBasicDataPanel
                    selfPayer={selfPayer}
                    agentUserProfile={users[serviceAssignment.createdBy]}
                    callbackPhoneNo={callbackPhoneNo}
                    fallbackSystemId={fallbackSystemId}
                    isFallbackSystemIdEnabled={isFallbackSystemIdEnabled}
                    additionalInformationText={additionalInformationText}
                    onChangeHandler={onChangeHandler}
                />

                <CommissioningCommissioningPanel
                    assignmentText={assignmentText}
                    onChangeHandler={onChangeHandler}
                />
                <CommissioningContactPersonPanel
                    title={translateTab('panel_title.contact_person_on_site')}
                    contactPerson={serviceAssignment.contactPersonOnSite}
                />
                <CommissioningContactPersonPanel
                    title={translateTab('panel_title.affected')}
                    contactPerson={serviceAssignment.affectedPerson}
                    isAffectedPerson={true}
                />
                <CommissioningDiagnoseAndInsurancePanel />
            </ContentItem>
            <ContentItem className={cx('ace-c-content-item--span-3')}>
                <CommissioningACEPartnerPanel
                    title={translateTab('panel_title.medical_advice')}
                    assignmentChannels={[
                        apmContractPartnerAssignmentChannelTypes.EMAIL_PDF_XML,
                        apmContractPartnerAssignmentChannelTypes.FAX,
                    ]}
                    handleOnAssignmentSubmit={handleOnSend}
                    attachmentsToSendWithAssignment={attachmentsToSendWithAssignment}
                    submitCreatePDFForm={generatePDF}
                />
                {!config.IS_FALLBACK_SYSTEM && (
                    <DocumentUploadPanel
                        attachmentsToSendWithAssignment={attachmentsToSendWithAssignment}
                        setAttachmentsToSendWithAssignment={setAttachmentsToSendWithAssignment}
                    />
                )}
            </ContentItem>
        </ContentBlock>
    );
};

SAMACommissioningTab.propTypes = {
    serviceAssignment: PropTypes.object,
    submitCreateSAMAPDF: PropTypes.func.isRequired,
    users: PropTypes.object,
    serviceCase: PropTypes.object,
    submitSAMACommissioningServiceProviderForm: PropTypes.func.isRequired,
    submitSAMACommissioningForm: PropTypes.func.isRequired,
    hasToResetAttachmentSelection: PropTypes.bool,
    storeHasToResetAttachmentSelection: PropTypes.func.isRequired,
};

SAMACommissioningTab.defaultProps = {
    serviceAssignment: null,
    users: null,
    serviceCase: null,
    hasToResetAttachmentSelection: false,
};

const mapStateToProps = (state, props) => {
    const serviceAssignmentSelector = serviceAssignmentSelectors.createServiceAssignmentSelector();
    const serviceCaseSelector = serviceCaseSelectors.createServiceCaseSelector();

    return {
        serviceAssignment: serviceAssignmentSelector(state, props),
        users: userProfileSelectors.getUsers(state),
        serviceCase: serviceCaseSelector(state, props),
        hasToResetAttachmentSelection: state.serviceAssignments.hasToResetAttachmentSelection,
    };
};

const mapDispatchToProps = dispatch => ({
    submitSAMACommissioningServiceProviderForm: payload => dispatch({
        type: serviceAssignmentActionTypes.SUBMIT_COMMISSIONING_ACE_PARTNER_FORM,
        payload,
    }),
    submitSAMACommissioningForm: payload => dispatch({
        type: samaActionTypes.SUBMIT_SAMA_COMMISSIONING_FORM,
        payload,
    }),
    submitCreateSAMAPDF: payload => dispatch({
        type: serviceAssignmentActionTypes.CREATE_SERVICE_ASSIGNMENT_PDF,
        payload,
    }),
    storeHasToResetAttachmentSelection: payload => dispatch({
        type: serviceAssignmentActionTypes.STORE_HAS_TO_RESET_ASSIGNMENT_SELECTION,
        payload,
    }),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SAMACommissioningTab));
